import type {
    FinalQuizInfoResponse,
    FinalQuizStatus,
} from '@routes/sidebar/learning/___pages___/learning-overview/api/finalQuiz.api.ts';
import type {
    LearningPracticeQuizResponse,
    PracticeQuizStatus,
    QuizNotification,
    QuizPracticeFinishResponse,
} from '@routes/sidebar/learning/___pages___/learning-overview/api/practiceQuiz.api.ts';
import type { LearningAdaptiveQuizSlice } from '@routes/sidebar/learning/___pages___/learning-overview/model/adaptiveQuiz.slice.ts';
import type { LearningFinalQuizSlice } from '@routes/sidebar/learning/___pages___/learning-overview/model/finalQuiz.slice.ts';
import type { LearningPracticeQuizSlice } from '@routes/sidebar/learning/___pages___/learning-overview/model/practiceQuiz.slice.ts';
import {
    ANSWER_STATUS,
    COMMON_QUIZ_STATUS,
    FINAL_QUIZ_STATUS,
    QUIZ_NOTIFICATION,
} from '@routes/sidebar/learning/___pages___/learning-overview/ui/overview/quiz/___shared___/constants.ts';

export const getIsChecked = (
    allChecked: Record<number, number[]>,
    questionId: number | null,
    answerId: number,
): boolean => {
    if (questionId === null) return false;

    return allChecked[questionId]?.includes(answerId) ?? false;
};

// Pick the last answered question; if none answered but quiz started, pick the first.
export const pickResumeQuestionId = (
    questions: LearningPracticeQuizResponse['questions'],
    quizStatus?: PracticeQuizStatus,
): number | null => {
    if (!questions || questions.length === 0) return null;

    // find last question (by backend order) with any userAnswers
    const lastAnswered = [...questions].reverse().find((q) => (q.userAnswers?.length ?? 0) > 0);
    if (lastAnswered) return lastAnswered.id;

    // nothing answered yet: if user already started, resume at first question
    if (quizStatus === COMMON_QUIZ_STATUS.STARTED || quizStatus === COMMON_QUIZ_STATUS.IN_PROGRESS) {
        return questions[0].id ?? null;
    }

    // NotStarted (or no questions) -> no current selection
    return null;
};

export const applyQuizPayload = (state: LearningPracticeQuizSlice, payload: LearningPracticeQuizResponse) => {
    const hasQuestions = payload.questions.length > 0;

    if (hasQuestions) {
        const questions = payload.questions;

        state.quizData = { ...payload, questions };

        // reset & hydrate maps from backend
        state.correctAnswers = {};
        state.checkedAnswers = {};
        state.answeredQuestions = [];
        state.quizQuestionAnswerStatuses = {};

        questions.forEach((q) => {
            state.correctAnswers[q.id] = q.correctAnswers ?? [];
            state.checkedAnswers[q.id] = q.userAnswers ?? [];
            state.quizQuestionAnswerStatuses[q.id] = q.quizQuestionAnswerStatus ?? ANSWER_STATUS.NOT_ANSWERED;

            if ((q.userAnswers ?? []).length > 0) state.answeredQuestions.push(q.id);
        });

        // resume at last answered; if none but started, at first; otherwise null
        state.currentQuestionId = pickResumeQuestionId(questions, payload.quizStatus);
    } else {
        state.quizData = { ...payload, questions: [] };
        state.currentQuestionId = null;
        state.quizQuestionAnswerStatuses = {};
    }
};

export const getAttemptsLeft = (attempt: number, maxAttempts: number): number => Math.max(maxAttempts - attempt, 0);

interface QuizScoreInfo {
    requiredScore: number;
    currentScore: number;
    highestScore: number;
}

export const getPracticeQuizScoreInfo = (quizFinishData: QuizPracticeFinishResponse | null): QuizScoreInfo | null => {
    if (quizFinishData) {
        return {
            requiredScore: quizFinishData.requiredPercentScore,
            currentScore: quizFinishData.currentPercentScore,
            highestScore: quizFinishData.highestPercentScore,
        };
    }

    return null;
};

export const getFinalQuizScoreInfo = (
    quizFinishData: QuizPracticeFinishResponse | FinalQuizInfoResponse | null,
    finalQuizIntroData: FinalQuizInfoResponse | null,
): QuizScoreInfo | null => {
    //case when user finish final quiz
    if (quizFinishData) {
        return {
            requiredScore: quizFinishData.requiredPercentScore,
            currentScore: quizFinishData.currentPercentScore,
            highestScore: quizFinishData.highestPercentScore,
        };
    }
    // case when user did refresh final quiz page or problem with internet
    if (finalQuizIntroData) {
        return {
            requiredScore: finalQuizIntroData.requiredPercentScore,
            currentScore: finalQuizIntroData.currentPercentScore,
            highestScore: finalQuizIntroData.highestPercentScore,
        };
    }

    return null;
};

export const applyAttemptsLeft = (
    state: LearningFinalQuizSlice | LearningAdaptiveQuizSlice,
    src?: { attempt?: number; maxAttempts?: number } | null,
) => {
    const attempt = src?.attempt ?? 0;
    const max = src?.maxAttempts ?? 0;
    state.attemptsLeft = getAttemptsLeft(attempt, max);
};

export const getSkippedQuestionNumbers = (
    skippedQuestions: Record<number, number[]>,
    quizId: number | null,
    questionOrder: number[],
): number[] => {
    if (quizId === null || !Array.isArray(questionOrder) || questionOrder.length === 0) return [];

    const skippedIds = skippedQuestions[Number(quizId)] ?? [];
    if (!skippedIds.length) return [];

    const nums = skippedIds
        .map((id) => questionOrder.indexOf(id))
        .filter((idx) => idx !== -1)
        .map((idx) => idx + 1);

    return Array.from(new Set(nums)).sort((a, b) => a - b);
};

export const isNoAttemptsLeftQuiz = (
    quizStatus: PracticeQuizStatus | FinalQuizStatus | undefined,
    quizNotification: QuizNotification | undefined,
): boolean =>
    quizStatus === FINAL_QUIZ_STATUS.NO_ATTEMPTS_LEFT || quizNotification === QUIZ_NOTIFICATION.NO_ATTEMPTS_LEFT;

// in quiz status we do not have NO_ATTEMPTS_LEFT so we need to handle it here
// case when user do f5/refresh/leave the page/ or any other action
export const getQuizStatusWithExceptions = (
    status: PracticeQuizStatus | FinalQuizStatus | undefined,
    quizNotification: QuizNotification | undefined,
) => {
    // case when isFinalQuiz=true and user do f5/refresh/leave the page/ or any other action
    if (status === COMMON_QUIZ_STATUS.IN_PROGRESS && quizNotification === QUIZ_NOTIFICATION.FAILED_WITH_CURRENT_SCORE) {
        return COMMON_QUIZ_STATUS.FAILED;
    }

    if (isNoAttemptsLeftQuiz(status, quizNotification)) {
        return FINAL_QUIZ_STATUS.NO_ATTEMPTS_LEFT;
    }

    return status;
};

export const removeQuestionFromSkippedOnes = (
    state: LearningPracticeQuizSlice | LearningFinalQuizSlice | LearningAdaptiveQuizSlice,
    questionId: number,
    quizId?: number,
) => {
    if (quizId !== undefined) {
        const list = state.skippedQuestions[quizId] ?? [];
        state.skippedQuestions[quizId] = list.filter((id) => id !== questionId);
    } else {
        for (const key of Object.keys(state.skippedQuestions)) {
            const k = Number(key);
            state.skippedQuestions[k] = state.skippedQuestions[k].filter((id) => id !== questionId);
        }
    }
};
